import { auth, IS_FIREBASE_CONFIGURED } from '../config/firebase';

// In dev, default to '' so requests hit the Vite proxy (/api → localhost:3001).
// In production, set VITE_API_URL to your deployed API origin (e.g. https://api.iomi.com).
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

/**
 * Builds a minimal unsigned JWT whose payload the backend's devDecodeUid() can parse.
 * Only used when Firebase credentials are absent (no web/.env configured).
 */
function makeLocalDevJwt(uid: string): string {
  const payload = btoa(JSON.stringify({ user_id: uid, sub: uid }))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
  return `eyJhbGciOiJub25lIn0.${payload}.dev`;
}

async function getToken(): Promise<string | null> {
  // When Firebase credentials aren't configured, getIdToken() silently hangs —
  // the SDK retries a token refresh that can never succeed. Skip it entirely and
  // hand back a dev JWT the backend will accept in its own dev mode.
  if (!IS_FIREBASE_CONFIGURED) {
    // auth is null when Firebase is not initialized — use a fixed dev UID.
    return makeLocalDevJwt('dev-local');
  }

  const user = auth.currentUser;
  if (!user) return null;

  // Real Firebase: race against a timeout so a broken refresh never stalls the UI.
  return new Promise<string>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('Cannot reach the backend server. Make sure it is running.')),
      8_000,
    );
    user.getIdToken().then(
      (t) => { clearTimeout(timer); resolve(t); },
      (e: unknown) => { clearTimeout(timer); reject(e); },
    );
  });
}

/** Mirrors ACTIVE_ORG_KEY in UserProfileContext; read here to avoid a context dependency. */
const ACTIVE_ORG_STORAGE_KEY = 'iomi_active_org_id';

function getActiveOrgId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  status: number;
  details?: string[];
  data?: Record<string, unknown>;
  constructor(message: string, status: number, details?: string[], data?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
    this.data = data;
  }
}

async function request<T>(method: string, path: string, body?: unknown, timeoutMs = 10_000): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  // Tell the backend which org the user is currently viewing so org-scoped
  // endpoints without an :orgId param (billing) act on that org rather than
  // on whichever membership they find first.
  const activeOrgId = getActiveOrgId();
  if (activeOrgId) headers['X-Org-Id'] = activeOrgId;

  // Abort the fetch after the given timeout so a hanging proxy/network never freezes the UI.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/api${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });
    } catch {
      throw new Error('Cannot reach the backend server. Make sure it is running.');
    }

    let data: ({ error?: string; details?: string[]; data?: Record<string, unknown> } & T) | undefined;
    try {
      data = (await res.json()) as { error?: string; details?: string[]; data?: Record<string, unknown> } & T;
    } catch {
      throw new Error(`Server returned ${res.status} with no JSON body`);
    }

    if (!res.ok) throw new ApiError(data.error ?? 'Request failed', res.status, data.details, data.data);
    return data as T;
  } finally {
    clearTimeout(timer);
  }
}

export interface RequestOptions {
  timeoutMs?: number;
}

export const api = {
  get:   <T>(path: string, opts?: RequestOptions)                    => request<T>('GET',    path, undefined, opts?.timeoutMs),
  post:  <T>(path: string, body?: unknown, opts?: RequestOptions)    => request<T>('POST',   path, body, opts?.timeoutMs),
  put:   <T>(path: string, body?: unknown, opts?: RequestOptions)    => request<T>('PUT',    path, body, opts?.timeoutMs),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions)    => request<T>('PATCH',  path, body, opts?.timeoutMs),
  del:   <T>(path: string, opts?: RequestOptions)                    => request<T>('DELETE', path, undefined, opts?.timeoutMs),
};
